import { autoinject, bindable } from 'aurelia-framework';
import { Router } from 'aurelia-router';
import Cookies from 'js-cookie';
import { State } from '../../services/global-state';
import { PayrazrService } from '../../services/payrazr';
import { ValidationHelpers } from '../../services/validation-helpers';
import { I18N } from 'aurelia-i18n';
import { PostLoginRouteManager } from '../../helpers/post-login-route-manager';
import { MfaSetupDetails } from '../../models/mfa/mfa-setup-details';
import { MfaTwoFactorMethod } from '../../models/mfa/mfa-two-factor-method';
import { MfaVerificationCode } from '../../models/mfa/mfa-verification-code';
import { DOM } from 'aurelia-pal';

@autoinject
export class MfaVerification {
	verificationCodeLabel: any;
	@bindable customerId: string = '';
	@bindable email: string = '';
	@bindable phoneNumber: string = '';
	@bindable isEzpay: boolean = false;
	@bindable merchantKey: number = 0;
	@bindable consumerUserCredentials: any;
	@bindable showRememberMe: boolean = true;
	twoFactorPreferredType: MfaTwoFactorMethod = MfaTwoFactorMethod.NotSet;
	maskedEmail: string = '';
	maskedPhone: string = '';
	model: MfaSetupDetails;
	verificationModel: MfaVerificationCode;
	isSendingVerificationCode: boolean = false;
	validationErrors: any = [];
	isVerifyingCode: boolean = false;
	verificationCode: string = '';
	rememberMeMfa: boolean = false;
	showCurrentNotificationMethods: boolean = false;
	forceDisableSendingCode: boolean = false;
	intervalIdForceDisableSendingCode: number;
	showVerification: boolean = false;
	newPhoneNumber: string = '';
	newEmail: string = '';
	notificationMethodsMessage: string = '';
	countDown: number = 60;
	timerCountDown: number = this.countDown;
	refreshIntervalMS: number = 1000; // 1 sec

	constructor(
		private payrazrService: PayrazrService,
		private globalState: State,
		private router: Router,
		private i18n: I18N,
		private postLoginRouteManager: PostLoginRouteManager,
		private element: Element
	) {
		this.model = new MfaSetupDetails();
		this.verificationModel = new MfaVerificationCode();
	}

	reset() {
		this.showVerification = false;
		this.twoFactorPreferredType = MfaTwoFactorMethod.NotSet;
		this.forceDisableSendingCode = false;
		clearInterval(this.intervalIdForceDisableSendingCode);
	}

	cancel() {
		this.router.navigateToRoute('logout');
	}

	attached() {
		if (!this.customerId) {
			this.router.navigateToRoute('logout');
		}
		else {
			this.notificationMethodsMessage = this.i18n.i18next.t('twoFactorConsumerPortalConfigMessage', {
				defaultValue: 'Multifactor authentication is now required. We will send a six-digit code to the email address or phone number from your profile below. After you log in, you may update the email address or phone number you use for authentication by selecting User Profile/Preferences on any screen. If you haven’t received the code within a couple of minutes, check your spam folder or select a different method if available.'
			});
			this.rememberMeMfa = false;
			this.verificationCodeLabel = '';
			this.verificationCode = '';
			this.maskNotificationMethods();
			this.setupNotificationMethods();
			this.setupMfaMessage();
		}
	}

	setupMfaMessage(): void {
		if (this.isEzpay) {
			this.notificationMethodsMessage = this.i18n.i18next.t('twoFactorConsumerPortalConfigMessageEzpay', {
				defaultValue: `Multifactor authentication is now required. We will send a six-digit code to the email address or phone number you provide. Select either mobile or email and then enter your phone number or email address below. Then click Send Verification Code to confirm. If you haven't received the code within a couple of minutes, check your spam folder or select a different method if available.`
			});
		} else {
			this.notificationMethodsMessage = this.i18n.i18next.t('twoFactorConsumerPortalConfigMessage', {
				defaultValue: 'Multifactor authentication is now required. We will send a six-digit code to the email address or phone number from your profile below. After you log in, you may update the email address or phone number you use for authentication by selecting User Profile/Preferences on any screen. If you haven’t received the code within a couple of minutes, check your spam folder or select a different method if available.'
			});
			if (!this.showCurrentNotificationMethods) {
				this.notificationMethodsMessage = this.i18n.i18next.t('twoFactorConsumerPortalConfigNoNotificationsMethods', {
					defaultValue: `Multifactor authentication is now required. We will send a six-digit code to the email address or phone number you provide. Please update your profile by selecting either mobile or email and then entering your phone number or email address below. Then click Send Verification Code to confirm. If you haven't received the code within a couple of minutes, check your spam folder or select a different method if available.`
				});
			}
		}
	}

	setupNotificationMethods(): void {
		const hasPhoneNumber = this.showMaskedPhone();
		const hasEmail = this.showMaskedEmail();

		if (hasPhoneNumber
			|| hasEmail) {
			if (!this.isEzpay) {
				this.showCurrentNotificationMethods = true;
				if (hasPhoneNumber && !hasEmail) {
					this.twoFactorPreferredType = MfaTwoFactorMethod.SMS;
				} else if (hasEmail && !hasPhoneNumber) {
					this.twoFactorPreferredType = MfaTwoFactorMethod.Email;
				}
			} else {
				if (hasEmail) {
					this.twoFactorPreferredType = MfaTwoFactorMethod.Email;
				} else if (hasPhoneNumber) {
					this.twoFactorPreferredType = MfaTwoFactorMethod.SMS;
				}
				this.newEmail = this.email;
				this.newPhoneNumber = this.phoneNumber;
			}
		} else {
			this.twoFactorPreferredType = MfaTwoFactorMethod.NotSet;
		}
		this.model.twoFactorMethod = this.twoFactorPreferredType;
	}

	maskNotificationMethods(): void {
		this.maskedEmail = this.maskEmailFormat(this.email);
		this.maskedPhone = this.maskPhoneFormat(this.phoneNumber);

		if (!this.isEzpay
			&& (this.maskedEmail !== ''
			|| this.maskedPhone !== ''))
		{
			this.showCurrentNotificationMethods = true;
		}
	}

	isUserOptIn(): boolean {
		return (!!this.maskedPhone
			&& (this.globalState.customer.notificationSettings?.smsIsOptedIn ?? false)
		);
	}

	maskPhoneFormat(value: string): string {
		if (!!value) {
			value = ValidationHelpers.sanitizePhoneNumber(value);
			const lastFourDigits = value.slice(-4);
			return `${''.padStart(3, '*')}-${''.padStart(3, '*')}-${''.padStart(4 - lastFourDigits.length, '*')}${lastFourDigits}`;
		}
		return '';
	}

	maskEmailFormat(value: string): string {
		if (!!value) {
			let emailParts = value.split('@');
			let address = emailParts[0];
			if (address.length < 2) {
				return ''.padStart(address.length, '*') + '@' + emailParts[1];
			}
			else if (address.length < 6) {
				return address[0] + ''.padStart(address.length - 1, '*') + '@' + emailParts[1];
			}
			else {
				return address[0] + ''.padStart(address.length - 2, '*') + address[address.length - 1] + '@' + emailParts[1];
			}
		}
		return '';
	}

	showMaskedEmail(): boolean {
		return !!this.maskedEmail;
	}

	showMaskedPhone(): boolean {
		return !!this.maskedPhone;
	}

	twoFactorPreferredTypeSelected(event, typeSelected): void {
		this.twoFactorPreferredType = typeSelected;
	}

	get isConfigurationModelValid(): boolean {

		if (this.twoFactorPreferredType === MfaTwoFactorMethod.NotSet
			|| this.forceDisableSendingCode) {
			return false;
		}

		if (!this.showCurrentNotificationMethods) {
			if (this.twoFactorPreferredType === MfaTwoFactorMethod.Email) {
				const value = this.newEmail;
				return !!value
					&& ValidationHelpers.isEmailAddress(value);
			} else {
				const value = this.newPhoneNumber;
				return !!value
					&& ValidationHelpers.isPhoneNumber(value);
			}
		}

		return true;
	}

	sendVerificationCode(): void {
		this.verificationCode = '';
		this.clearValidationErrors();
		this.isSendingVerificationCode = true;
		this.showVerification = false;
		this.model.twoFactorMethod = this.twoFactorPreferredType;
		if (!this.showCurrentNotificationMethods) {
			if (this.twoFactorPreferredType === MfaTwoFactorMethod.SMS) {
				this.model.twoFactorValue = this.newPhoneNumber;
			} else {
				this.model.twoFactorValue = this.newEmail;
			}
		}
		this.model.forceTwoFactorValue = this.isEzpay || (!this.showCurrentNotificationMethods
			&& !!this.model.twoFactorValue);
		this.verificationModel = new MfaVerificationCode();

		if (this.isEzpay) {
			this.model.merchantKey = this.merchantKey;
			this.sendVerificationCodeEzPay();
			return;
		}

		this.model.username = this.consumerUserCredentials .username;
		this.model.password = this.consumerUserCredentials .password;

		this.payrazrService.sendMfaVerificationCode(this.model, this.customerId).then(response => {
			this.isSendingVerificationCode = false;
			if (response.isSuccessful) {
				this.showVerification = true;
				this.disableSendVerificationCode();
				this.refreshVerificationCodeLabel();
				this.verificationModel.forceTwoFactorValue = this.model.forceTwoFactorValue;
				this.verificationModel.twoFactorMethod = this.model.twoFactorMethod;
				this.verificationModel.twoFactorValue = this.model.twoFactorValue;
			}
			else {
				const errorMessage = !!response.message
				? response.message
				: this.i18n.i18next.t('twoFactorConsumerPortalVerificationCodeSendError', 'Error encountered when attempting to send verification code.');
				this.validationErrors.push(errorMessage);
			}
		}).catch(error => {
			this.isSendingVerificationCode = false;
			this.validationErrors.push(this.i18n.i18next.t('twoFactorConsumerPortalVerificationCodeSendError', 'Error encountered when attempting to send verification code.'));
		});
	}

	sendVerificationCodeEzPay(): void {
		this.payrazrService.sendUnvalidatedEzPayVerificationCode(this.customerId, this.model).then(response => {
			this.isSendingVerificationCode = false;
			if (response.isSuccessful) {
				this.showVerification = true;
				this.disableSendVerificationCode();
				this.refreshVerificationCodeLabel();
				this.verificationModel.forceTwoFactorValue = this.model.forceTwoFactorValue;
				this.verificationModel.twoFactorMethod = this.model.twoFactorMethod;
				this.verificationModel.twoFactorValue = this.model.twoFactorValue;
			} else {
				const errorMessage = !!response.message
					? response.message
					: this.i18n.i18next.t('twoFactorConsumerPortalVerificationCodeSendError', 'Error encountered when attempting to send verification code.');
				this.validationErrors.push(errorMessage);
			}
		}).catch(error => {
			this.isSendingVerificationCode = false;
			this.validationErrors.push(this.i18n.i18next.t('twoFactorConsumerPortalVerificationCodeSendError', 'Error encountered when attempting to send verification code.'));
		});
	}

	clearValidationErrors(): void {
		this.validationErrors = [];
	}

	get getTwoFactorValue(): string {
		if (this.model.twoFactorMethod == MfaTwoFactorMethod.NotSet) {
			return '';
		}

		if (this.showCurrentNotificationMethods) {
			if (this.model.twoFactorMethod == MfaTwoFactorMethod.Email) {
				return this.maskedEmail;
			}
			return this.maskedPhone;
		}

		if (!!this.model.twoFactorValue) {
			if (this.model.twoFactorMethod == MfaTwoFactorMethod.Email) {
				return this.maskEmailFormat(this.model.twoFactorValue);
			}
			return this.maskPhoneFormat(this.model.twoFactorValue);
		} else {
			if (this.model.twoFactorMethod == MfaTwoFactorMethod.Email) {
				return this.maskedEmail;
			}
			return this.maskedPhone;
		}
	}

	get isVerificationModelValid(): boolean {
		return this.verificationModel.twoFactorMethod != MfaTwoFactorMethod.NotSet
			&& !!this.verificationCode
			&& this.verificationCode.length == 6
			&& ValidationHelpers.isOnlyDigits(this.verificationCode);
	}

	verify(): void {
		this.clearValidationErrors();
		if (this.isVerificationModelValid) {
			this.isVerifyingCode = true;
			this.verificationModel.forceTwoFactorValue = this.model.forceTwoFactorValue;
			this.verificationModel.twoFactorMethod = this.model.twoFactorMethod;
			this.verificationModel.twoFactorValue = this.model.twoFactorValue;
			this.verificationModel.code = this.verificationCode;

			if (this.isEzpay) {
				this.verifyCodeEzpay();
				return;
			}

			this.verificationModel.username = this.consumerUserCredentials .username;
			this.verificationModel.password = this.consumerUserCredentials.password;
			this.verificationModel.rememberMe = this.rememberMeMfa;

			this.payrazrService.verifyMfaVerificationCode(this.verificationModel, this.customerId)
				.then(response => {
					this.isVerifyingCode = false;
					if (response.isSuccessful) {
						clearInterval(this.intervalIdForceDisableSendingCode);

						if (response.entity && response.entity.phoneMobileVerified) {
							this.globalState.customer.notificationSettings.smsIsValidated = true;
						}

						this.globalState.customer.mfaSettings.twoFactorEnabled = true;
						this.globalState.customer.mfaSettings.preferredTwoFactorMethod = this.verificationModel.twoFactorMethod;
						this.globalState.customer.mfaSettings.twoFactorVerified = true;
						this.handleRemember({
							id: this.verificationModel.username,
							rememberMeName: response.entity.cookieName,
							rememberMeExpiration: response.entity.cookieExpiration,
							rememberMeValue: response.entity.cookieValue,
						});
						this.redirectAfterVerify({ token: response.entity.token, customer: response.entity.customer, mustChangePassword: response.entity.isChangePasswordRequired });
					}
					else {
						const errorMessage = response.message ?? this.i18n.i18next.t('twoFactorConsumerPortalVerificationCodeSendError', 'Error encountered when attempting to send verification code.');
						this.validationErrors.push(errorMessage);
					}
				}).catch(error => {
					this.isVerifyingCode = false;
					this.validationErrors.push(this.i18n.i18next.t('twoFactorConsumerPortalVerificationError', 'Error encountered while attempting to verify the code you entered.'));
				});
		}
		else {
			this.validationErrors.push(this.i18n.i18next.t('enterVerificationCode', 'Please enter verification code.'));
		}
	}

	verifyCodeEzpay(): void {
		this.payrazrService.verifyUnvalidatedEzPayVerificationCode(this.customerId, this.verificationModel)
			.then(response => {
				this.isVerifyingCode = false;
				if (response.isSuccessful) {
					clearInterval(this.intervalIdForceDisableSendingCode);
					this.redirectAfterVerify();
				}
				else {
					const errorMessage = response.message ?? this.i18n.i18next.t('twoFactorConsumerPortalVerificationCodeSendError', 'Error encountered when attempting to send verification code.');
					this.validationErrors.push(errorMessage);
				}
			}).catch(error => {
				this.isVerifyingCode = false;
				this.validationErrors.push(this.i18n.i18next.t('twoFactorConsumerPortalVerificationError', 'Error encountered while attempting to verify the code you entered.'));
			});
	}

	disableSendVerificationCode(): void {
		this.forceDisableSendingCode = true;
		this.timerCountDown = this.countDown;
		this.intervalIdForceDisableSendingCode = window.setInterval(() => this.displayCountDown(), this.refreshIntervalMS);
	}

	displayCountDown() {
		if (this.timerCountDown <= 1) {
			this.forceDisableSendingCode = false;
			clearInterval(this.intervalIdForceDisableSendingCode);
			return;
		}
		this.timerCountDown -= 1;
	}

	redirectAfterVerify(details: any = {}): void {
		const eventName = 'mfa-verify-valid';
		let eventData: any = {};
		if (this.isEzpay) {
			eventData = {
				verificationCode: this.verificationModel.code,
				mfaValue: this.verificationModel.twoFactorValue,
				mfaType: this.verificationModel.twoFactorMethod
			};
		} else {
			eventData = details;
		}
		const eventToTrigger = DOM.createCustomEvent(
			eventName,
			{ bubbles: true, cancelable: true, detail: eventData });

		this.reset();
		this.element.dispatchEvent(eventToTrigger);
	}

	setupVerificationView(): void {
		this.twoFactorPreferredType = this.globalState.customer.mfaSettings.preferredTwoFactorMethod;
		this.model.twoFactorMethod = this.twoFactorPreferredType;
		this.refreshVerificationCodeLabel();
		this.sendVerificationCode();
	}

	refreshVerificationCodeLabel(): void {
		this.verificationCodeLabel = this.i18n.i18next.t('twoFactorConsumerPortalEnterToken', {
			defaultValue: `Please enter the code sent to: ${this.getTwoFactorValue}.  This code is valid for five minutes.`,
			twoFacthorMethodValue: this.getTwoFactorValue
		});
	}

	handleRemember({ id, rememberMeName, rememberMeExpiration, rememberMeValue }): void {
		const lstCookies = this.postLoginRouteManager.getCookiesByPrefix(id);
		lstCookies.forEach((cname) => {
			Cookies.remove(cname);
		});
		if (this.rememberMeMfa && rememberMeName && rememberMeValue  && rememberMeExpiration > 0) {
			Cookies.set(rememberMeName, rememberMeValue, { expires: new Date(rememberMeExpiration) });
		}
	}

}
